import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCpu, FiToggleLeft, FiToggleRight, FiSearch, FiChevronRight, FiDatabase, FiUsers, FiHome, FiLayers, FiBook, FiMessageSquare, FiAlertTriangle, FiClock, FiThumbsUp, FiBarChart2 } from 'react-icons/fi';
import request from '../utils/request';

export default function AIConciergeScreen() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [hosts, setHosts] = useState([]);
  const [conciergeStats, setConciergeStats] = useState(null);
  const [topQuestions, setTopQuestions] = useState([]);
  const [recentConversations, setRecentConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, hostsRes, analyticsRes, questionsRes, convsRes] = await Promise.all([
        request.get('/studio/api/ai-stats'),
        request.get('/studio/api/hosts'),
        request.get('/studio/api/concierge-analytics').catch(() => ({ data: null })),
        request.get('/studio/api/concierge-top-questions?limit=5').catch(() => ({ data: [] })),
        request.get('/studio/api/concierge-conversations?limit=5').catch(() => ({ data: { conversations: [] } })),
      ]);
      setStats(statsRes.data);
      setHosts(hostsRes.data);
      setConciergeStats(analyticsRes.data);
      setTopQuestions(questionsRes.data || []);
      setRecentConversations(convsRes.data?.conversations || []);
    } catch (err) {
      console.error('Failed to load AI concierge data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredHosts = useMemo(() => {
    let list = hosts;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(h =>
        `${h.firstName} ${h.lastName}`.toLowerCase().includes(q) ||
        (h.email || '').toLowerCase().includes(q)
      );
    }
    if (filterStatus === 'enabled') list = list.filter(h => h.aiConciergeEnabled);
    if (filterStatus === 'disabled') list = list.filter(h => !h.aiConciergeEnabled);
    return list;
  }, [hosts, search, filterStatus]);

  const toggleHost = useCallback(async (hostId) => {
    try {
      const res = await request.patch(`/studio/api/hosts/${hostId}/ai-toggle`);
      setHosts(prev => prev.map(h => h._id === hostId ? { ...h, aiConciergeEnabled: res.data.aiConciergeEnabled } : h));
      setStats(prev => {
        if (!prev) return prev;
        const delta = res.data.aiConciergeEnabled ? 1 : -1;
        const active = prev.activeHosts + delta;
        return { ...prev, activeHosts: active, activePercent: Math.round((active / prev.totalHosts) * 100) };
      });
    } catch (err) {
      console.error('Toggle failed:', err);
    }
  }, []);

  const executeBulkToggle = useCallback(async (enabled) => {
    setBulkLoading(true);
    setConfirmModal(null);
    try {
      await request.patch('/studio/api/hosts/bulk-ai-toggle', { enabled });
      await fetchData();
    } catch (err) {
      console.error('Bulk toggle failed:', err);
    } finally {
      setBulkLoading(false);
    }
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-40">
        <div className="w-8 h-8 border-4 border-zinc-200 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = stats ? [
    { label: 'Total Hosts', value: stats.totalHosts, icon: FiUsers, color: 'bg-blue-50 text-blue-600', iconBg: 'bg-blue-100' },
    { label: 'AI Active', value: `${stats.activeHosts} (${stats.activePercent}%)`, icon: FiCpu, color: 'bg-emerald-50 text-emerald-600', iconBg: 'bg-emerald-100' },
    { label: 'Properties w/ AI', value: stats.propertiesWithAI, icon: FiHome, color: 'bg-violet-50 text-violet-600', iconBg: 'bg-violet-100' },
    { label: 'Field Mappings', value: `${stats.enabledMappings} / ${stats.totalMappings}`, icon: FiDatabase, color: 'bg-amber-50 text-amber-600', iconBg: 'bg-amber-100' },
    { label: 'Guidebook Active', value: `${stats.guidebookActiveHosts} (${stats.guidebookActivePercent}%)`, icon: FiBook, color: 'bg-blue-50 text-blue-600', iconBg: 'bg-blue-100' },
  ] : [];

  const conciergeStatCards = conciergeStats ? [
    { label: 'Total Conversations', value: conciergeStats.totalConversations, icon: FiMessageSquare, iconBg: 'bg-indigo-100', iconColor: 'text-indigo-600' },
    { label: 'Escalation Rate', value: `${Math.round(conciergeStats.escalationRate * 100)}%`, icon: FiAlertTriangle, iconBg: 'bg-orange-100', iconColor: 'text-orange-600' },
    { label: 'Avg Response', value: conciergeStats.avgResponseTimeMs > 0 ? `${(conciergeStats.avgResponseTimeMs / 1000).toFixed(1)}s` : '—', icon: FiClock, iconBg: 'bg-sky-100', iconColor: 'text-sky-600' },
    { label: 'Feedback Score', value: conciergeStats.feedbackScore > 0 ? `${conciergeStats.feedbackScore}%` : '—', icon: FiThumbsUp, iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600' },
  ] : [];

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <FiCpu className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">AI Concierge</h1>
                <p className="text-sm text-zinc-500">Manage AI-powered guest assistance across all hosts</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/concierge-analytics')}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 transition-colors"
            >
              <FiBarChart2 className="w-4 h-4" />
              Full Analytics
            </button>
          </div>
        </div>

        {/* Host Management Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {statCards.map((card) => (
            <div key={card.label} className="bg-white rounded-2xl p-5 border border-zinc-100 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400">{card.label}</span>
                <div className={`w-9 h-9 rounded-xl ${card.iconBg} flex items-center justify-center`}>
                  <card.icon className={`w-4 h-4 ${card.color.split(' ')[1]}`} />
                </div>
              </div>
              <p className="text-2xl font-bold text-zinc-900">{card.value}</p>
            </div>
          ))}
        </div>

        {/* Concierge Analytics Cards */}
        {conciergeStatCards.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {conciergeStatCards.map((card) => (
              <div key={card.label} className="bg-white rounded-2xl p-5 border border-zinc-100 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400">{card.label}</span>
                  <div className={`w-9 h-9 rounded-xl ${card.iconBg} flex items-center justify-center`}>
                    <card.icon className={`w-4 h-4 ${card.iconColor}`} />
                  </div>
                </div>
                <p className="text-2xl font-bold text-zinc-900">{card.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Top Unanswered Questions + Recent Conversations Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Top Escalated Questions */}
          <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-zinc-100">
              <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
                <FiAlertTriangle className="w-4 h-4 text-orange-500" />
                Top Unanswered Questions
              </h2>
              <p className="text-xs text-zinc-500 mt-0.5">Questions that triggered escalation</p>
            </div>
            <div className="divide-y divide-zinc-50">
              {topQuestions.length > 0 ? topQuestions.map((q, i) => (
                <div key={i} className="px-5 py-3 flex items-center justify-between">
                  <p className="text-sm text-zinc-700 truncate flex-1 mr-3">{q.question}</p>
                  <span className="text-xs font-semibold text-zinc-400 bg-zinc-100 px-2 py-0.5 rounded-full whitespace-nowrap">{q.count}x</span>
                </div>
              )) : (
                <div className="px-5 py-8 text-center text-sm text-zinc-400">No escalated questions yet</div>
              )}
            </div>
          </div>

          {/* Recent Conversations */}
          <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-zinc-100">
              <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
                <FiMessageSquare className="w-4 h-4 text-indigo-500" />
                Recent Conversations
              </h2>
              <p className="text-xs text-zinc-500 mt-0.5">Latest guest interactions</p>
            </div>
            <div className="divide-y divide-zinc-50">
              {recentConversations.length > 0 ? recentConversations.map((conv) => (
                <div
                  key={conv._id}
                  className="px-5 py-3 flex items-center justify-between hover:bg-zinc-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/concierge-analytics?conversation=${conv._id}`)}
                >
                  <div className="flex-1 min-w-0 mr-3">
                    <p className="text-sm font-medium text-zinc-700 truncate">{conv.propertyUniqueId}</p>
                    <p className="text-xs text-zinc-400">{conv.messageCount} messages &middot; {new Date(conv.lastMessageAt).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {conv.escalated && <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">Escalated</span>}
                    <FiChevronRight className="w-3.5 h-3.5 text-zinc-300" />
                  </div>
                </div>
              )) : (
                <div className="px-5 py-8 text-center text-sm text-zinc-400">No conversations yet</div>
              )}
            </div>
          </div>
        </div>

        {/* Bulk Controls */}
        <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm p-5 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-zinc-900">Bulk Controls</h2>
              <p className="text-sm text-zinc-500 mt-0.5">Enable or disable AI Concierge for all hosts at once</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmModal('enable')}
                disabled={bulkLoading}
                className="px-4 py-2 rounded-xl text-sm font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 transition-colors disabled:opacity-50"
              >
                Enable All
              </button>
              <button
                onClick={() => setConfirmModal('disable')}
                disabled={bulkLoading}
                className="px-4 py-2 rounded-xl text-sm font-medium bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 transition-colors disabled:opacity-50"
              >
                Disable All
              </button>
            </div>
          </div>
        </div>

        {/* Filters + Host Table */}
        <div className="bg-white rounded-2xl border border-zinc-100 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-zinc-100">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="relative flex-1 w-full sm:max-w-xs">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search hosts..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 outline-none transition-all"
                />
              </div>
              <div className="flex gap-2">
                {['all', 'enabled', 'disabled'].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilterStatus(f)}
                    className={`px-3.5 py-2 rounded-xl text-xs font-medium transition-colors capitalize ${
                      filterStatus === f
                        ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                        : 'bg-zinc-50 text-zinc-600 border border-zinc-200 hover:bg-zinc-100'
                    }`}
                  >
                    {f} {f !== 'all' && `(${f === 'enabled' ? hosts.filter(h => h.aiConciergeEnabled).length : hosts.filter(h => !h.aiConciergeEnabled).length})`}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50/80">
                  <th className="text-left px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Host</th>
                  <th className="text-left px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Email</th>
                  <th className="text-center px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">AI Concierge</th>
                  <th className="text-right px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {filteredHosts.map((host) => (
                  <tr
                    key={host._id}
                    className="hover:bg-zinc-50/60 transition-colors cursor-pointer"
                    onClick={() => navigate(`/ai-concierge/${host._id}`)}
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-zinc-100 to-zinc-200 flex items-center justify-center text-xs font-bold text-zinc-600">
                          {(host.firstName || '?')[0]}{(host.lastName || '?')[0]}
                        </div>
                        <span className="font-medium text-zinc-900">{host.firstName} {host.lastName}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-zinc-500">{host.email || '—'}</td>
                    <td className="px-5 py-3.5 text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleHost(host._id); }}
                        className="inline-flex items-center gap-1.5"
                      >
                        {host.aiConciergeEnabled ? (
                          <>
                            <FiToggleRight className="w-6 h-6 text-emerald-500" />
                            <span className="text-xs font-medium text-emerald-600">Active</span>
                          </>
                        ) : (
                          <>
                            <FiToggleLeft className="w-6 h-6 text-zinc-300" />
                            <span className="text-xs font-medium text-zinc-400">Off</span>
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/ai-concierge/${host._id}`); }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
                      >
                        Details <FiChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredHosts.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-5 py-12 text-center text-zinc-400 text-sm">
                      No hosts found matching your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="px-5 py-3 border-t border-zinc-100 bg-zinc-50/40 text-xs text-zinc-400">
            Showing {filteredHosts.length} of {hosts.length} hosts
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={() => setConfirmModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm mx-4 w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-zinc-900 mb-2">
              {confirmModal === 'enable' ? 'Enable All Hosts?' : 'Disable All Hosts?'}
            </h3>
            <p className="text-sm text-zinc-500 mb-6">
              {confirmModal === 'enable'
                ? `This will enable AI Concierge for all ${hosts.length} hosts. Are you sure?`
                : `This will disable AI Concierge for all ${hosts.length} hosts. Are you sure?`}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmModal(null)}
                className="px-4 py-2 rounded-xl text-sm font-medium text-zinc-600 bg-zinc-100 hover:bg-zinc-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => executeBulkToggle(confirmModal === 'enable')}
                className={`px-4 py-2 rounded-xl text-sm font-medium text-white transition-colors ${
                  confirmModal === 'enable' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {confirmModal === 'enable' ? 'Enable All' : 'Disable All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
